import { Suspense, forwardRef, useRef } from "react"
import { enqueueSnackbar } from "notistack"
import { useNavigate } from "react-router"
import { FormikProps } from "formik"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE, SUPPLIER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { createMerchant, SupplierMerchantQuery } from "../supplierMerchantsApi"
import { CreateUpdateMerchantFormValues } from "../supplierMerchantsTypes"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateMerchantForm } from "../components/CreateUpdateMerchantForm"

const MerchantContent = forwardRef<FormikProps<CreateUpdateMerchantFormValues>>((props, ref) => {
    const navigate = useNavigate()

    const { mutateAsync: callCreateMerchant } = useApiMutation(createMerchant, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupplierMerchantQuery.LIST] })
            if ("serviceResponse" in data && data.serviceResponse.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse.displayMessage,
                })
            }
            navigate(SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE)
        },
    })

    return <CreateUpdateMerchantForm ref={ref} onSubmit={callCreateMerchant} />
})

export function SupplierMerchantCreate() {
    const merchantFormRef = useRef<FormikProps<CreateUpdateMerchantFormValues>>(null)

    const handleSaveClick = () => {
        if (merchantFormRef.current) {
            merchantFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="supplier-admin-merchant-create-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_MERCHANTS_CREATE}
                    title="Create merchant"
                    rightElement={
                        <LocalizedStrict id="supplier-admin-merchants-create-button">
                            <StandardButton onClick={handleSaveClick}>Create</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants">
                            <Breadcrumb>Merchants</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE}>Merchants</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants-create">
                            <Breadcrumb>Create merchant</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <MerchantContent ref={merchantFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
